import type { MedalEventState } from "@seagames/contracts";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Where a medal event stands (contracts' `MEDAL_EVENT_STATES`), one look for
 * every screen: a colour for marks (`STATE_COLOR`, the validated chart tokens),
 * and a chip with its own GLYPH and WORD — never colour alone.
 *
 *   NOT_SCHEDULED  ○ hollow ring        "Not scheduled"
 *   COMPETING      ▶ play               "Competing"
 *   WAITING        ◔ clock              "Waiting for the desk"
 *   AWARDED        ✓ tick               "Awarded"
 *   NO_MEDALS      ⊘ slashed circle     "No medals"
 */

export const STATE_COLOR: Record<MedalEventState, string> = {
	NOT_SCHEDULED: "var(--color-state-not-scheduled)",
	COMPETING: "var(--color-state-competing)",
	WAITING: "var(--color-state-waiting)",
	AWARDED: "var(--color-state-awarded)",
	NO_MEDALS: "var(--color-state-no-medals)",
};

const g = (children: ReactNode) => (
	<svg viewBox="0 0 12 12" aria-hidden="true" className="size-[0.75em] shrink-0" fill="none">
		{children}
	</svg>
);

export const STATE_GLYPH: Record<MedalEventState, ReactNode> = {
	NOT_SCHEDULED: g(<circle cx="6" cy="6" r="4.25" stroke="currentColor" strokeWidth="1.5" />),
	COMPETING: g(<path d="M3.5 2.25v7.5L9.75 6Z" fill="currentColor" />),
	WAITING: g(
		<>
			<circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
			<path d="M6 3.5V6l1.75 1.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
		</>,
	),
	AWARDED: g(<path d="m2 6.5 2.75 2.75L10 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />),
	NO_MEDALS: g(
		<>
			<circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
			<path d="m3 9 6-6" stroke="currentColor" strokeWidth="1.5" />
		</>,
	),
};

const LOOK: Record<MedalEventState, string> = {
	NOT_SCHEDULED: "bg-paper text-muted ring-rule",
	COMPETING: "bg-list-wash text-list ring-list/25",
	WAITING: "bg-wait-wash text-wait ring-wait/30",
	AWARDED: "bg-done-wash text-done ring-done/25",
	NO_MEDALS: "bg-[#efeafa] text-[#5b3fa0] ring-[#5b3fa0]/25",
};

export function StateChip({ state, count, className = "" }: { state: MedalEventState; count?: number; className?: string }) {
	const { t } = useTranslation("medals");
	return (
		<span
			data-state={state}
			className={`inline-flex items-center gap-[0.375em] whitespace-nowrap rounded-full px-[0.625em] py-[0.125em] font-bold text-[0.75rem] leading-[1.35] ring-1 ring-inset ${LOOK[state]} ${className}`}
		>
			{STATE_GLYPH[state]}
			<span>{t(`state.${state}`)}</span>
			{count !== undefined && <span className="font-mono tabular-nums">{count}</span>}
		</span>
	);
}
